import React, { useState } from 'react';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { ImageIcon } from 'lucide-react';

import FlexColumn from 'src/components/flex/FlexColumn';
import CopyTextButton from 'src/components/copy-text-button/CopyTextButton';

import styles from 'src/components/lead-sidebar/FactRow.module.css';

interface Props {
    label: string;
    value: string;
    factKey: string;
    /** Copied to the clipboard instead of `value`, e.g. when `value` is truncated for display. */
    copyText?: string;
}

enum FactType {
    IMAGE_LINK,
    LINK,
    STRING,
};

const IMG_EXTENSIONS = new Set(['jpeg', 'jpg', 'png', 'webp']);

/**
 * Infer the type of the fact value based on value contents.
 *
 * NOTE: a known limitation of this method is image detection only works if the link pathname contains an image contains an extension
 * URLs such as https://example.com/images/123 WILL NOT WORK.
 */
const inferType = (value: string): FactType => {
    value = value.trim();
    const isLink = (value.startsWith('https://') || value.startsWith('http://')) && !value.includes(' ');
    if (!isLink) {
        return FactType.STRING;
    }
    try {
        const u = new URL(value);
        // read only pathname, not query params
        const parts = u.pathname.split('.');
        if (parts.length === 1) {
            // no extension
            return FactType.LINK;
        }

        const ext = parts[parts.length - 1].toLowerCase();
        if (IMG_EXTENSIONS.has(ext)) {
            return FactType.IMAGE_LINK;
        } else {
            return FactType.LINK;
        }
    } catch {
        // if new URL fails
        return FactType.STRING;
    }
};

function FactLink({ value }: { value: string }) {
    let domain = value;
    try { domain = new URL(value).hostname; } catch { /* keep value */ }

    return <Link href={value} target="_blank" rel="noreferrer noopener" referrerPolicy="no-referrer" variant="body2">
        <span className={styles.linkRow}>
            {domain} <OpenInNewOutlinedIcon className={styles.externalLinkIcon} />
        </span>
    </Link>;
}

interface IImagePreviewLinkProps {
    value: string;
    isModalOpen: boolean;
    setModalOpen(v: boolean): void;
}

function FactImagePreviewLink({ isModalOpen, setModalOpen }: IImagePreviewLinkProps) {
    const handleClick = (e: React.SyntheticEvent) => {
        e.preventDefault();

        if (!isModalOpen) {
            setModalOpen(true);
        }
    };

    return <Link href="#" onClick={handleClick} data-testid="open-fact-image-modal-btn">
        <span>
            <ImageIcon className={styles.openImagePreviewIcon} size={12} />
            Show
        </span>
    </Link>;
}

function FactImageModal({ label, value, onClose }: { label: string, value: string, onClose(): void }) {
    return <Dialog open onClose={onClose} className={styles.factImageModal}>
        <DialogTitle>{label} </DialogTitle>
        <DialogContent>
            <FlexColumn width="100%" flexWrap="nowrap">
                <img src={value} alt={label} referrerPolicy="no-referrer" />
            </FlexColumn>
        </DialogContent>
        <DialogActions>
            <Button variant="outlined" color="dark" size="small" onClick={onClose}>
                Close
            </Button>
        </DialogActions>
    </Dialog>;
}

export default function FactRow({ label, value, factKey, copyText }: Props) {
    const [isImagePreviewOpen, setImagePreviewOpen] = useState<boolean>(false);

    value = value.trim();
    const inferredType = inferType(value);
    let valueElem;

    switch (inferredType) {
        case FactType.LINK:
            valueElem = <FactLink value={value} />;
            break;
        case FactType.IMAGE_LINK:
            valueElem = <FactImagePreviewLink value={value} isModalOpen={isImagePreviewOpen} setModalOpen={setImagePreviewOpen} />;
            break;
        default:
            valueElem = <Typography variant="body2">{value}</Typography>;
            break;
    }

    return (
        <div className={styles.factRow}>
            {isImagePreviewOpen && <FactImageModal label={label} value={value} onClose={() => setImagePreviewOpen(false)} />}
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <div className={styles.factValueRow}>
                {valueElem}
                <CopyTextButton text={copyText ?? value} className={styles.copyFactButton} data-testid={`copy-fact-${factKey}`} />
            </div>
        </div>
    );
}
